/**
 * Corridor sides — SINGLE SOURCE OF TRUTH for "which side of the corridor is a
 * country on?" on the website.
 *
 * EuroCollis is no longer Morocco-only: the model is Africa ↔ Europe, with
 * Morocco and Tunisia live and Algeria planned. No module may test a country
 * by comparing it to the literal string "Morocco" — use these helpers so a new
 * corridor country works by adding a DB row, not by editing code.
 *
 * The canonical country list lives in `public.countries` / `public.corridor_pairs`.
 * This module only classifies an ISO code (or an English country name, for the
 * static SEO datasets that predate the DB) onto the African side.
 */

import { CATALOGUE_COUNTRIES } from "@/lib/regionCatalogue";

/**
 * African corridor side, derived from the Europe/Africa catalogue so opening a
 * new country in Admin never needs a code change. Europe is "everything else
 * that is supported".
 */
export const AFRICA_ISO: ReadonlySet<string> = new Set(
  CATALOGUE_COUNTRIES.filter((c) => c.region === "africa").map((c) => c.iso),
);

/** English names used by the static SEO route datasets. */
export const AFRICA_COUNTRY_NAMES: ReadonlySet<string> = new Set([
  ...CATALOGUE_COUNTRIES.filter((c) => c.region === "africa").map((c) => c.name),
  // Legacy spellings kept by the static SEO datasets.
  "Ivory Coast",
]);

export function isAfricaSideIso(iso?: string | null): boolean {
  return AFRICA_ISO.has(String(iso ?? "").trim().toUpperCase());
}

export function isAfricaSideCountry(name?: string | null): boolean {
  return AFRICA_COUNTRY_NAMES.has(String(name ?? "").trim());
}

/**
 * A corridor crosses the Africa ↔ Europe divide when exactly one endpoint is on
 * the African side. Same-country pairs (Casablanca → Rabat) and intra-Europe
 * pairs (Lyon → Madrid) are not corridors.
 */
export function isCrossCorridorIsoPair(
  originIso?: string | null,
  destinationIso?: string | null,
): boolean {
  const a = String(originIso ?? "").trim().toUpperCase();
  const b = String(destinationIso ?? "").trim().toUpperCase();
  if (!a || !b) return false;
  if (a === b) return false;
  return isAfricaSideIso(a) !== isAfricaSideIso(b);
}
